import classNames from 'classnames'
import i18next from 'i18next'
import { KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from 'react'
import { Badge, ButtonBase, ButtonVariant, Icon, IconButton, IconName, Text } from '../../atoms'

export interface FilterOverlayProps {
  label?: string
  closeLabel?: string
  activeFilterCount?: number
  isOpen?: boolean
  setIsOpen?: (open: boolean) => void
  onClearFilters?: () => void
  disableClearFilters?: boolean
  disabled?: boolean
  icon?: IconName
  small?: boolean
  buttonVariant?: ButtonVariant
  panelClassName?: string
  children: ReactNode
}

export const FilterOverlay = ({
  label = i18next.t('glow.filterOverlay.filter'),
  closeLabel = i18next.t('glow.close'),
  activeFilterCount = 0,
  isOpen,
  setIsOpen,
  onClearFilters,
  disableClearFilters = activeFilterCount === 0,
  disabled,
  icon,
  small = true,
  buttonVariant = 'secondary',
  panelClassName,
  children
}: FilterOverlayProps) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false)
  const open = isOpen ?? uncontrolledIsOpen
  const setOpen = setIsOpen ?? setUncontrolledIsOpen
  const isExpanded = open && !disabled

  const panelId = useId()
  const titleId = useId()
  const toggleRef = useRef<HTMLButtonElement>(null)
  // React events bubble through portals, so a dropdown that renders its menu in a portal still counts as inside.
  const insidePointerDown = useRef<Event | null>(null)

  const closeAndFocusToggle = () => {
    setOpen(false)
    toggleRef.current?.focus()
  }

  useEffect(() => {
    if (!isExpanded) return
    const onPointerDown = (event: PointerEvent) => {
      if (event !== insidePointerDown.current) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [isExpanded, setOpen])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isExpanded || event.key !== 'Escape' || event.defaultPrevented) return
    // An open dropdown inside the panel owns Escape. Some, like FormSelect, close it from a window listener that runs after this one.
    const expandedPopup = (event.target as Element).closest('[aria-expanded="true"]')
    if (expandedPopup && expandedPopup !== toggleRef.current) return
    event.preventDefault()
    closeAndFocusToggle()
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: catches Escape and pointerdown bubbling up from the overlay's own controls
    <div
      className="relative"
      onPointerDownCapture={(event) => {
        insidePointerDown.current = event.nativeEvent
      }}
      onKeyDown={onKeyDown}
    >
      <ButtonBase
        ref={toggleRef}
        variant={buttonVariant}
        small={small}
        type="button"
        disabled={disabled}
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? panelId : undefined}
        aria-label={
          activeFilterCount > 0
            ? `${label}, ${i18next.t('glow.filterOverlay.activeFilterCount', { count: activeFilterCount })}`
            : undefined
        }
        onClick={() => setOpen(!open)}
      >
        <span className="flex h-full items-center">
          <Icon icon={icon ?? (isExpanded ? 'chevron-up' : 'chevron-down')} />
          {label}
          {activeFilterCount > 0 && <Badge variant="WARNING" text={activeFilterCount} className="ml-2" />}
        </span>
      </ButtonBase>

      {isExpanded && (
        <section
          id={panelId}
          aria-labelledby={titleId}
          className={classNames(
            'absolute right-0 top-full mt-2 z-20 rounded border border-solid border-gray-500 bg-gray-100',
            panelClassName
          )}
        >
          <div className="flex items-center justify-between px-6 py-3 border-b border-solid border-gray-500">
            <Text as="h2" id={titleId} textType="sm-black" semiBold>
              {label}
            </Text>
            <IconButton size="sm" icon="close" aria-label={closeLabel} onClick={closeAndFocusToggle} />
          </div>
          {children}
          {onClearFilters && (
            <div className="border-t border-solid border-gray-500 px-6 py-3 flex justify-end">
              <ButtonBase small variant="tertiary" disabled={disableClearFilters} onClick={onClearFilters}>
                {i18next.t('glow.filterOverlay.clearFilter')}
              </ButtonBase>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
